import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  apiErrText, rmb, getMe, getPackageSubcontracts, myWorkInstructions, myWorkInstruction, staffCompliancePackage,
  issueWorkInstruction,
  staffWiLedger, staffWiDetail, staffWiDayworkRecords, setSubcontractContractForm,
  voidWorkInstruction, confirmInstructionRework, verifyDayworkRecord,
  instructionDocumentUrl, instructionPhotoUrl, countdown, dayworkCategoryText, parseJsonArray,
  CONTRACT_FORM_LABEL,
  DAYWORK_STATE, WI_KIND_LABEL, WI_ORIGIN_LABEL, WI_STATE,
  type ContractForm, type DayworkRecord, type InstructionDetail,
  type MeResponse, type MyInstructionItem, type StaffComplianceView, type SubcontractRow, type WorkItem,
} from '../lib/api';
import { Skeleton } from './ui/Skeleton';
import { EmptyState } from './ui/EmptyState';
import { toast } from './ui/Toast';
import InstructionEditor from './InstructionEditor';
import InstructionSignatureSeats from './InstructionSignatureSeats';
import InstructionPricingSheet, { canPriceInstruction } from './InstructionPricingSheet';
import InstructionApprovalCard from './InstructionApprovalCard';
import ContractValueBlock from './ContractValueBlock';

/**
 * 📝 工程指令 / Instructions — the staff ledger for one signed subcontract (Layer 2 PR8, Eric 9/18).
 *
 * The header answers the question the 2026-07 meeting was called about: what is this contract actually
 * worth now, after everything we ordered mid-job? Two numbers, never touching — 基准合同价 (frozen, and
 * the ONLY basis for the 4.2.3/4.2.4 payment ceilings) and 当前核定合同价 (advisory). Saying that plainly
 * under the figure is the whole point; a lone "current value" is how a progress payment goes wrong.
 *
 * 项目经理 (site_pm) branch: the PM signs the order (9.1.1) and must never see what it costs (改改 7/14).
 * So for a PM this panel NEVER calls a money route — not the ledger, not the daywork list — and builds
 * its rows from the price-free `/me/work-instructions` queue instead, filtered to this package. There
 * are no amount columns, no header block, no pricing sheet and no approval card in that branch at all.
 */

const PRICE_ROLES = ['gm', 'budget'];
const APPROVE_ROLES = ['gm', 'commercial'];
const VERIFY_ROLES = ['gm', 'budget'];

export default function InstructionsPanel({ pid, workItems }: { pid: string; workItems: WorkItem[] }) {
  const qc = useQueryClient();
  const { data: me } = useQuery<MeResponse>({ queryKey: ['me'], queryFn: getMe });
  const role = me?.account?.role ?? '';
  const isPm = role === 'site_pm';
  const [params, setParams] = useSearchParams();
  const focusWi = params.get('wi');

  // The subcontract list is itself a money route — a PM never gets here (see the PM branch below).
  const { data: subs } = useQuery<SubcontractRow[]>({
    queryKey: ['subcontracts', pid],
    queryFn: () => getPackageSubcontracts(pid),
    enabled: !!pid && !isPm && me?.account?.subject_type === 'staff',
  });
  // 合同形式 lives on the 履约档案 read (Layer 1), not on the subcontract list — same query key the
  // package page already uses for its 待审 pill, so this is a cache hit rather than a second request.
  const { data: compliance } = useQuery<StaffComplianceView>({
    queryKey: ['compliance', 'package', pid],
    queryFn: () => staffCompliancePackage(pid),
    enabled: !!pid && !isPm && me?.account?.subject_type === 'staff',
  });
  const signed = (subs ?? []).filter((s) => s.status === 'signed');
  const [scId, setScId] = useState<string | null>(null);
  const activeSc = signed.find((s) => s.id === (scId ?? signed[0]?.id)) ?? null;

  if (!me?.authenticated) return <div className="card">请先登录 / please sign in.</div>;
  if (isPm) return <PmInstructionsView pid={pid} me={me} focusWi={focusWi} onFocus={(id) => setParams((p) => { if (id) p.set('wi', id); else p.delete('wi'); return p; }, { replace: true })} />;

  if (!subs) return <div className="card"><Skeleton lines={5} /></div>;
  if (!signed.length) {
    return (
      <div className="card">
        <EmptyState title="合同尚未签署 / No signed subcontract"
          hint="工程指令要有已签署的分包合同才能下达 —— 先在「验收标准与合同」页生成并记录签署。/ instructions need a signed subcontract; generate and record the executed contract first." />
      </div>
    );
  }

  return (
    <div>
      {signed.length > 1 && (
        <div className="row-wrap" style={{ gap: 6, marginBottom: 10 }}>
          {signed.map((s) => (
            <button key={s.id} className={`tab${activeSc?.id === s.id ? ' active' : ''}`} onClick={() => setScId(s.id)}>
              {s.labor_co_name ?? s.id}
            </button>
          ))}
        </div>
      )}
      {activeSc && (
        <SubcontractInstructions
          sc={activeSc}
          // `/packages/:pid/subcontracts` does not select contract_form; the 履约档案 read does, and
          // PackageDetail already holds it under this exact query key, so this costs no extra request.
          // review 9/18: the subcontract list carries contract_form now (and reload() invalidates it) — the
          // 履约档案 read is only a fallback, and only for the subcontract it describes
          contractForm={(activeSc as { contract_form?: 'unit_price' | 'lump_sum' | null }).contract_form
            ?? (compliance?.subcontract_id === activeSc.id ? compliance?.contract_form ?? null : null)}
          workItems={workItems}
          me={me}
          focusWi={focusWi}
          onFocus={(id) => setParams((p) => { if (id) p.set('wi', id); else p.delete('wi'); return p; }, { replace: true })}
          reload={() => {
            qc.invalidateQueries({ queryKey: ['wi-ledger', activeSc.id] });
            qc.invalidateQueries({ queryKey: ['subcontracts', pid] });
    qc.invalidateQueries({ queryKey: ['compliance', 'package', pid] });
            qc.invalidateQueries({ queryKey: ['my-work-instructions'] });
          }}
        />
      )}
    </div>
  );
}

// ── the money ledger ────────────────────────────────────────────────────────────────────────────

function SubcontractInstructions({ sc, contractForm, workItems, me, focusWi, onFocus, reload }: {
  sc: SubcontractRow;
  contractForm: ContractForm | null;
  workItems: WorkItem[];
  me: MeResponse;
  focusWi: string | null;
  onFocus: (id: string | null) => void;
  reload: () => void;
}) {
  const [editing, setEditing] = useState<'new-variation' | 'new-daywork' | { draft: InstructionDetail } | null>(null);
  const [busy, setBusy] = useState(false);
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['wi-ledger', sc.id],
    queryFn: () => staffWiLedger(sc.id),
    // A 404/403 here is an answer, not a flake — retrying three times only delays the honest banner.
    retry: false,
  });

  async function setForm(v: ContractForm) {
    setBusy(true);
    try { await setSubcontractContractForm(sc.id, { contract_form: v }); toast('合同形式已设置 / contract form set', { kind: 'success' }); reload(); }
    catch (e) { toast(apiErrText(e), { kind: 'error' }); }
    finally { setBusy(false); }
  }

  if (isLoading) return <div className="card"><Skeleton lines={6} /></div>;

  // The LEDGER is a money route; 新建 / 签字 / 下发 are not. A money-route outage must never stop anyone
  // writing an order — the contract's own rule is that unwritten work is neither measured nor paid, so
  // "the amounts list is down" is the worst possible reason to work off the books. The toolbar and the
  // editor render either way; only the figures disappear.
  const cv = data?.contract_value ?? null;
  // The ledger sends `seats` ONCE, for the subcontract, not on every row — so graft it onto each item.
  // Without this a DRAFT shows 「未指派」 on all three cards even right after they were assigned: a draft
  // has no signature rows yet, and the signature rows are the only other place a holder's name lives.
  const rows = (data?.items ?? []).map((d) => (d.seats ? d : { ...d, seats: data?.seats }));   // seq DESC — newest first

  return (
    <div>
      {cv && <ContractValueBlock cv={cv} />}

      <div className="card" style={{ marginBottom: 10 }}>
        <div className="row-wrap" style={{ alignItems: 'center', gap: 10 }}>
          <strong>📝 工程指令 / Work instructions</strong>
          {sc.labor_co_name && <span className="pill pill-stage small">{sc.labor_co_name}</span>}
          <span className="small muted">
            合同形式 / form: {contractForm ? CONTRACT_FORM_LABEL[contractForm] : '未知 / unknown'}
          </span>
          {!contractForm && (
            <select disabled={busy} defaultValue="" style={{ minHeight: 44 }}
              onChange={(e) => { const v = e.target.value as ContractForm | ''; if (v) setForm(v); }}>
              <option value="">设置合同形式 / set it…</option>
              <option value="unit_price">{CONTRACT_FORM_LABEL.unit_price}</option>
              <option value="lump_sum">{CONTRACT_FORM_LABEL.lump_sum}</option>
            </select>
          )}
          <span style={{ flex: 1 }} />
          <button style={{ minHeight: 44 }} onClick={() => setEditing('new-variation')}>＋ 新建工程指令 / New instruction</button>
          <button style={{ minHeight: 44 }} onClick={() => setEditing('new-daywork')}>＋ 新建零星工程指令 / New daywork order</button>
        </div>
        {!contractForm && (
          <div className="small" style={{ color: 'var(--bad)', marginTop: 6 }}>
            合同形式未确定 —— 零星（计日工）指令无法下达（服务器 409 contract_form_unknown）。/ daywork cannot be issued until the contract form is set.
          </div>
        )}
      </div>

      {editing && (
        <InstructionEditor
          scId={sc.id}
          contractForm={contractForm}
          workItems={workItems}
          isPm={false}
          existing={typeof editing === 'object' ? editing.draft : undefined}
          initialKind={editing === 'new-daywork' ? 'daywork' : 'variation'}
          onClose={() => setEditing(null)}
          onSaved={() => reload()}
        />
      )}

      {isError && (
        <div className="card" style={{ marginBottom: 10 }}>
          <div style={{ color: 'var(--bad)' }}>台账（含金额）读取失败 / the amounts ledger could not be read: {apiErrText(error)}</div>
          <div className="small muted" style={{ marginTop: 6 }}>
            台账读取失败，请刷新重试；新建 / 签字 / 下发 不受影响。
            / the ledger could not be read — refresh and retry; create / sign / issue keep working.
          </div>
        </div>
      )}

      {rows.length === 0
        ? !isError && <div className="card"><EmptyState title="暂无工程指令 / No instructions yet" hint="现场发生变更或零星用工时，在这里开一条书面指令。未经书面指令擅自施工的，发包人不予计量、不予支付。/ open a written order here — unwritten work is not measured and not paid." /></div>
        : (
          <div className="cmp-wrap" style={{ marginBottom: 10 }}>
            <table className="cmp">
              <thead>
                <tr>
                  <th>序号</th><th>类型</th><th>状态</th><th>发生日</th><th>报价期限</th>
                  <th>申报</th><th>审核</th><th>认价</th><th>操作</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => <LedgerRow key={d.instruction.id} d={d} expanded={focusWi === d.instruction.id}
                  onToggle={() => onFocus(focusWi === d.instruction.id ? null : d.instruction.id)} />)}
              </tbody>
            </table>
          </div>
        )}

      {rows.filter((d) => focusWi === d.instruction.id).map((d) => (
        <ExpandedInstruction key={d.instruction.id} row={d} workItems={workItems} me={me} reload={reload}
          onEditDraft={(full) => setEditing({ draft: full })} />
      ))}

      {/* A deep link from /me/instructions must open even when the row is not in THIS ledger — the money
          half may be unavailable, or the order may belong to another subcontract on the package. The
          fallback read is the price-free /me route, so it degrades to less information, never to a
          dead link. */}
      {focusWi && !rows.some((d) => d.instruction.id === focusWi) && (
        <FocusedInstructionFallback wiid={focusWi} workItems={workItems} me={me} reload={reload} />
      )}
    </div>
  );
}

/**
 * One expanded row. The LEDGER is deliberately thin — it carries totals and chips for every order on the
 * contract — so opening one re-reads `GET /work-instructions/:wiid`, which is where the 认价单 versions,
 * the 材料询价 summaries and the photos live. The ledger row renders immediately underneath as the
 * fallback, so the card never flashes empty while that read is in flight.
 */
function ExpandedInstruction({ row, workItems, me, reload, onEditDraft }: {
  row: InstructionDetail;
  workItems: WorkItem[];
  me: MeResponse;
  reload: () => void;
  onEditDraft: (full: InstructionDetail) => void;
}) {
  const qc = useQueryClient();
  const wiid = row.instruction.id;
  const { data } = useQuery({ queryKey: ['wi-detail', wiid], queryFn: () => staffWiDetail(wiid), retry: false });
  const detail = data ? { ...row, ...data } : row;
  const refresh = () => { qc.invalidateQueries({ queryKey: ['wi-detail', wiid] }); reload(); };
  return (
    <InstructionDetailCard detail={detail} workItems={workItems} me={me} reload={refresh}
      onEditDraft={() => onEditDraft(detail)} />
  );
}

/** Price-free detail for an order the money ledger did not return. */
function FocusedInstructionFallback({ wiid, workItems, me, reload }: {
  wiid: string; workItems: WorkItem[]; me: MeResponse; reload: () => void;
}) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['me-work-instruction', wiid],
    queryFn: () => myWorkInstruction(wiid),
  });
  const qc = useQueryClient();
  const refresh = () => { qc.invalidateQueries({ queryKey: ['me-work-instruction', wiid] }); reload(); };
  if (isLoading) return <div className="card"><Skeleton lines={4} /></div>;
  if (isError) return <div className="card small">无法打开该指令 / could not open this instruction: {apiErrText(error)}</div>;
  if (!data) return null;
  return (
    <div>
      <div className="small muted" style={{ marginBottom: 4 }}>
        以下为价格无关视图（台账未返回该条）/ price-free view — this order was not in the amounts ledger.
      </div>
      <InstructionDetailCard detail={data} workItems={workItems} me={me} reload={refresh} onEditDraft={() => undefined} />
    </div>
  );
}

// Eric 9/18 (工程指令): §1.11's header block moved to components/ContractValueBlock.tsx in PR10b — the
// 执行与结算 tab and the payment certs show the same figures under the same caveat, and one copy of that
// sentence is the point. `ContractValueHeader` was its only renderer; it is now an import.

/**
 * Overdue chips read BOTH ways: what 乙方 owes us, and what WE owe them (G6 — a blown WTL SLA is shown
 * as loudly as a blown contractor one, which is why the ledger has two columns of chips, not one).
 * The flags are PR7's `overdueFlags()`, derived server-side on every read; the raw timestamps below are
 * the fallback for a ledger built before PR7's flags reach the serializer.
 */
function OverdueChips({ d }: { d: InstructionDetail }) {
  const wi = d.instruction;
  const o = d.overdue ?? null;
  const out: { text: string; title: string }[] = [];
  const hrs = (n: number | null | undefined) => (n == null ? '' : ` · ${Math.round(Math.abs(n))}h${n < 0 ? '逾期' : '剩余'}`);
  // 失权 hangs off quote_forfeited — the FACT the sweep recorded — not off quote_overdue, which is only
  // "the clock has run out"; a late-served order blows the clock without ever extinguishing the right.
  if (o?.quote_forfeited ?? !!wi.quote_forfeited_at) out.push({ text: '劳务报价逾期失权', title: '合同 9.1.3 — 逾期未报，工程量不应计量、费用不应补偿' });
  else if (o?.quote_overdue) out.push({ text: `劳务报价逾期${hrs(o.quote_hours_left)}`, title: '合同 9.1.3 — 7 天报价期限已过，扫描尚未做失权处理' });
  if (o?.late_service ?? wi.late_service) out.push({ text: '我方送达迟于报价期限', title: 'G13 — 我方送达晚于报价截止，失权被抑制，商务已知会' });
  if (o?.pricing_overdue) out.push({ text: `我方审核逾期${hrs(o.pricing_hours_left)}`, title: 'G6 — WTL 审核报价 7 天' });
  if (o?.verify_overdue) out.push({ text: '我方计日工核实逾期', title: 'G6 — 计日工核实 2 工作日' });
  if (o?.rework_overdue) out.push({ text: '返工确认逾期', title: 'G3 — 返工完成确认 3 工作日，不会自动通过' });
  if (!out.length) return null;
  return <>{out.map((c) => <span key={c.text} className="pill pill-bad small" title={c.title} style={{ marginLeft: 4 }}>{c.text}</span>)}</>;
}

/** 报价期限 cell. A 零星（计日工）order has NO quote clock at all — `quote_due_at` is null by design. */
function QuoteDueCell({ d }: { d: InstructionDetail }) {
  const wi = d.instruction;
  if (wi.kind === 'daywork') return <span className="muted">不适用（按日报）/ n/a — daily sheets</span>;
  if (wi.quote_submitted_at) return <>已报价 / quoted</>;
  const cd = countdown(wi.quote_due_at);
  return <span style={{ color: cd.expired ? 'var(--bad)' : cd.urgent ? 'var(--accent)' : undefined }}>{cd.text}</span>;
}

function LedgerRow({ d, expanded, onToggle }: { d: InstructionDetail; expanded: boolean; onToggle: () => void }) {
  const wi = d.instruction;
  const st = WI_STATE[wi.state];
  const declared = d.totals?.declared_rmb ?? d.lines.reduce((s, l) => s + (l.declared_amount_rmb ?? 0), 0);
  const audited = d.totals?.audited_rmb ?? d.lines.reduce((s, l) => s + (l.audited_amount_rmb ?? 0), 0);
  return (
    <tr>
      <td>{wi.doc_no ?? wi.seq}</td>
      <td>{WI_KIND_LABEL[wi.kind]}</td>
      <td>
        <span className={`pill ${st.pill} small`}>{st.label}</span>
        <OverdueChips d={d} />
      </td>
      <td>{wi.occurred_on}</td>
      <td><QuoteDueCell d={d} /></td>
      <td>{declared ? rmb(declared) : '—'}</td>
      <td>{audited ? rmb(audited) : '—'}</td>
      <td>{wi.confirmed_at ? rmb(wi.approved_amount_rmb ?? audited) : '—'}</td>
      <td><button className="small" style={{ minHeight: 44 }} onClick={onToggle}>{expanded ? '收起 / Close' : '展开 / Open'}</button></td>
    </tr>
  );
}

// ── the expanded card ───────────────────────────────────────────────────────────────────────────

function InstructionDetailCard({ detail, workItems, me, reload, onEditDraft }: {
  detail: InstructionDetail;
  workItems: WorkItem[];
  me: MeResponse;
  reload: () => void;
  onEditDraft: () => void;
}) {
  const wi = detail.instruction;
  const role = me.account?.role ?? '';
  const st = WI_STATE[wi.state];
  const [busy, setBusy] = useState(false);
  const canPrice = PRICE_ROLES.includes(role) && canPriceInstruction(detail);
  const canApprove = APPROVE_ROLES.includes(role);

  async function doIssue() {
    setBusy(true);
    try {
      await issueWorkInstruction(wi.id);
      toast('已下发，等待三方签字 / issued — awaiting the three signatures', { kind: 'success' });
      reload();
    } catch (e) { toast(apiErrText(e), { kind: 'error' }); }
    finally { setBusy(false); }
  }

  async function doVoid() {
    const reason = prompt('作废理由（必填）/ reason for voiding (required):');
    if (!reason || !reason.trim()) return;
    setBusy(true);
    try { await voidWorkInstruction(wi.id, reason.trim()); toast('已作废 / voided', { kind: 'success' }); reload(); }
    catch (e) { toast(apiErrText(e), { kind: 'error' }); }
    finally { setBusy(false); }
  }

  return (
    <div className="card" style={{ marginBottom: 12, borderColor: 'var(--accent)' }}>
      <div className="row-wrap" style={{ alignItems: 'center', gap: 10 }}>
        <strong>{wi.doc_no ?? `#${wi.seq}`} · {wi.title}</strong>
        <span className={`pill ${st.pill} small`}>{st.label}</span>
        <span className="small muted">{WI_KIND_LABEL[wi.kind]} · {WI_ORIGIN_LABEL[wi.origin ?? ''] ?? wi.origin ?? '—'} · {wi.occurred_on}</span>
        <span style={{ flex: 1 }} />
        {wi.state !== 'draft' && <a className="btn small" style={{ minHeight: 44 }} href={instructionDocumentUrl(wi.id, 'instruction')}>⬇ 指令单 .docx</a>}
        {(wi.state === 'priced' || wi.state === 'approved' || wi.state === 'confirmed' || wi.state === 'disputed') && (
          <a className="btn small" style={{ minHeight: 44 }} href={instructionDocumentUrl(wi.id, 'price_confirmation')}>⬇ 认价单 .docx</a>
        )}
        {wi.state === 'draft' && <button className="small" style={{ minHeight: 44 }} onClick={onEditDraft}>✎ 编辑草稿 / Edit draft</button>}
        {/* 下发 belongs on the row too: the seats are assigned HERE, and making somebody reopen the editor
            to press the same button is how a draft sits unissued while the site works off an unwritten order. */}
        {wi.state === 'draft' && (
          <button className="btn-primary small" style={{ minHeight: 44 }} disabled={busy} onClick={doIssue}>📤 下发 / Issue</button>
        )}
        {/* review 9/18: no 作废 where the server refuses — never on disputed/approved/confirmed, and once served only gm/商务 */}
        {!['confirmed', 'approved', 'void', 'disputed'].includes(wi.state)
          && (!wi.served_at || me?.account?.role === 'gm' || me?.account?.role === 'commercial') && (
          <button className="small" style={{ minHeight: 44, color: 'var(--bad)' }} disabled={busy} onClick={doVoid}>🗑 作废 / Void</button>
        )}
      </div>

      {/* the state's own contract sentence, under the pill (§3) */}
      {st.note && <div className="small" style={{ color: 'var(--bad)', marginTop: 6 }}>{st.note}</div>}

      <div className="row-wrap small" style={{ gap: 16, marginTop: 8 }}>
        {wi.served_at && <span>送达 / served {new Date(wi.served_at).toLocaleString('zh-CN')}</span>}
        {wi.read_at && <span>劳务已读 / read {new Date(wi.read_at).toLocaleString('zh-CN')}</span>}
        {wi.kind === 'variation' && wi.quote_due_at && <span>报价期限 / quote due {countdown(wi.quote_due_at).text}</span>}
        {wi.daywork_category && <span>零星类别 / category {dayworkCategoryText(wi.daywork_category)}</span>}
        {wi.drawing_ref && <span>图纸变更号 / drawing {wi.drawing_ref}</span>}
        {wi.estimate_rmb != null && <span>预估 / estimate {rmb(wi.estimate_rmb)}</span>}
        {wi.pre_approved_at && <span className="pill pill-good small">商务已确认 / 商务 acknowledged</span>}
      </div>
      {wi.reason_md && <div className="small" style={{ marginTop: 6 }}><strong>事由 / reason:</strong> {wi.reason_md}</div>}
      {wi.scope_md && <div className="small"><strong>内容 / scope:</strong> {wi.scope_md}</div>}
      {wi.location_md && <div className="small"><strong>部位 / location:</strong> {wi.location_md}</div>}
      {wi.void_reason_md && <div className="small" style={{ color: 'var(--bad)' }}>作废理由 / void reason: {wi.void_reason_md}</div>}
      {wi.dispute_note_md && <div className="small" style={{ color: 'var(--bad)' }}>劳务异议 / dispute: {wi.dispute_note_md}</div>}

      <InstructionSignatureSeats detail={detail} me={me} onChanged={reload} />

      {canPrice && <InstructionPricingSheet detail={detail} workItems={workItems} onChanged={reload} />}
      {(wi.state === 'priced' || wi.approved_at) && <InstructionApprovalCard detail={detail} canApprove={canApprove} onChanged={reload} />}

      {wi.rework_required && !wi.rework_confirmed_at && wi.rework_notified_at && (
        <ReworkConfirm wiid={wi.id} dueAt={wi.rework_confirm_due_at} onDone={reload} />
      )}

      {wi.kind === 'daywork' && <DayworkList wiid={wi.id} canVerify={VERIFY_ROLES.includes(role)} onChanged={reload} />}

      {(detail.photos ?? []).length > 0 && (
        <div className="row-wrap small" style={{ gap: 8, marginTop: 8 }}>
          <span className="muted">现场照片 / photos:</span>
          {(detail.photos ?? []).map((p) => (
            <a key={p.id} href={instructionPhotoUrl(p.id)} target="_blank" rel="noreferrer">{p.kind}{p.taken_on ? ` · ${p.taken_on}` : ''} ↗</a>
          ))}
        </div>
      )}
    </div>
  );
}

/** 9.1.4 — a rework order closes only with a completion photo; the G3 clock never auto-passes. */
function ReworkConfirm({ wiid, dueAt, onDone }: { wiid: string; dueAt: string | null; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState('');
  const cd = countdown(dueAt);
  return (
    <div className="card" style={{ marginBottom: 10, background: 'var(--warn-soft)' }}>
      <div className="row-wrap" style={{ alignItems: 'center', gap: 10 }}>
        <strong>🔁 返工完成确认 / Rework confirmation</strong>
        <span className={`small`} style={{ color: cd.expired ? 'var(--bad)' : undefined }}>{cd.text}</span>
      </div>
      <div className="small muted" style={{ margin: '4px 0 6px' }}>合同 9.1.4 — 须上传完成照片（影像资料），逾期不会自动通过。/ a completion photo is required; the clock never auto-passes.</div>
      <div className="row-wrap" style={{ gap: 8, alignItems: 'flex-end' }}>
        <div className="field" style={{ flex: '1 1 220px', marginBottom: 0 }}><label>说明 / notes</label><input value={notes} onChange={(e) => setNotes(e.target.value)} style={{ minHeight: 44 }} /></div>
        <input type="file" accept="image/*,application/pdf" disabled={busy}
          onChange={async (e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (!f) return;
            setBusy(true);
            try {
              const fd = new FormData();
              fd.append('photo', f);
              if (notes.trim()) fd.append('notes_md', notes.trim());
              await confirmInstructionRework(wiid, fd);
              toast('返工已确认 / rework confirmed', { kind: 'success' });
              onDone();
            } catch (err) { toast(apiErrText(err), { kind: 'error' }); }
            finally { setBusy(false); }
          }} />
      </div>
    </div>
  );
}

// ── 计日工 (PR5) ─────────────────────────────────────────────────────────────────────────────────

function DayworkList({ wiid, canVerify, onChanged }: { wiid: string; canVerify: boolean; onChanged: () => void }) {
  const [open, setOpen] = useState<DayworkRecord | null>(null);
  const { data, isError, error, refetch } = useQuery({
    queryKey: ['wi-daywork', wiid],
    queryFn: () => staffWiDayworkRecords(wiid),
    retry: false,
  });
  if (isError) {
    return <div className="card small muted" style={{ marginBottom: 10 }}>计日工日报读取失败 / daywork list unavailable: {apiErrText(error)}</div>;
  }
  const rows = data?.records ?? [];
  return (
    <div className="card" style={{ marginBottom: 10 }}>
      <div className="row-wrap" style={{ alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <strong>📅 计日工日报 / Daywork records</strong>
        <span className="small muted">合同 9.2.11 — 当日作业须于次日前上报，48 小时未报即失权 / report by the next day; 48 h and the right is gone</span>
      </div>
      {/* 固定总价: the order is only enforceable with a 补充协议 — the server sends the sentence, verbatim. */}
      {data?.banner_zh && (
        <div className="small" style={{ background: 'var(--warn-soft)', border: '1px solid var(--warn)', padding: '8px 10px', borderRadius: 6, marginBottom: 8 }}>
          ⚠ {data.banner_zh}
        </div>
      )}
      {rows.length === 0 && <div className="small muted">暂无日报 / none submitted yet.</div>}
      {rows.map((r) => {
        const st = DAYWORK_STATE[r.state];
        // `persons` is parsed server-side; the raw column rides along only as a fallback.
        const persons = r.persons ?? parseJsonArray<{ name?: string }>(r.persons_json);
        return (
          <div key={r.id} className="row-wrap small" style={{ gap: 10, alignItems: 'center', borderTop: '1px solid var(--rule)', padding: '6px 0' }}>
            <strong>{r.work_date}</strong>
            <span className={`pill ${st.pill} small`}>{st.label}</span>
            <span className="muted">{persons.length} 人 / {persons.length} on site</span>
            {r.priced_amount_rmb != null && <span>{rmb(r.priced_amount_rmb)}</span>}
            {r.submission_overdue && r.state === 'open' && <span className="pill pill-warn small" title="9.2.11 — 应于次日前上报">逾期未报</span>}
            {r.verify_overdue && <span className="pill pill-bad small" title="G6 — 我方核实 2 工作日">我方核实逾期</span>}
            {r.state === 'forfeited' && <span className="small" style={{ color: 'var(--bad)' }}>{r.contract_text_zh ?? st.note}</span>}
            <span style={{ flex: 1 }} />
            {canVerify && r.state === 'submitted' && (
              <button className="small" style={{ minHeight: 44 }} onClick={() => setOpen(r)}>核实 / Verify</button>
            )}
            {r.verified_at && <span className="muted">已核实 {r.verified_by ?? ''} · {new Date(r.verified_at).toLocaleDateString('zh-CN')}</span>}
          </div>
        );
      })}
      {open && <DayworkVerifyDrawer record={open} onClose={() => setOpen(null)} onDone={() => { setOpen(null); refetch(); onChanged(); }} />}
    </div>
  );
}

/**
 * 核实 — 预算 may CORRECT the crew/material/plant the contractor reported; the original is kept in
 * `submitted_snapshot_json` server-side and audited, so a correction is never a silent overwrite.
 */
function DayworkVerifyDrawer({ record, onClose, onDone }: { record: DayworkRecord; onClose: () => void; onDone: () => void }) {
  const [persons, setPersons] = useState<Record<string, unknown>[]>(() => (record.persons ?? parseJsonArray(record.persons_json)) as Record<string, unknown>[]);
  const [materials, setMaterials] = useState<Record<string, unknown>[]>(() => (record.materials ?? parseJsonArray(record.materials_json)) as Record<string, unknown>[]);
  const [equipment, setEquipment] = useState<Record<string, unknown>[]>(() => (record.equipment ?? parseJsonArray(record.equipment_json)) as Record<string, unknown>[]);
  const [note, setNote] = useState('');
  const [priced, setPriced] = useState('');
  const [basis, setBasis] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    if (!note.trim()) { setErr('请填写核实说明 / a verification note is required'); return; }
    setBusy(true); setErr(null);
    try {
      await verifyDayworkRecord(record.id, {
        verify_note_md: note.trim(),
        persons_json: persons, materials_json: materials, equipment_json: equipment,
        priced_amount_rmb: priced === '' ? null : Number(priced),
        pricing_basis: basis.trim() || null,
      });
      toast('日报已核实 / daywork verified', { kind: 'success' });
      onDone();
    } catch (e) { setErr(apiErrText(e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="card drawer" style={{ borderColor: 'var(--accent)', marginTop: 8 }}>
      <div className="row-wrap" style={{ alignItems: 'center', gap: 8 }}>
        <strong>核实 {record.work_date} 日报 / Verify the daywork sheet</strong>
        <span style={{ flex: 1 }} />
        <button className="small" style={{ minHeight: 44 }} onClick={onClose}>× 关闭 / Close</button>
      </div>
      <div className="small muted" style={{ margin: '4px 0 8px' }}>
        可修改数量，原报内容会完整留痕。/ corrections are allowed — the contractor's original submission is kept and audited.
      </div>
      {record.reason_md && <div className="small"><strong>事由 / reason:</strong> {record.reason_md}</div>}
      {record.work_content_md && <div className="small"><strong>工作内容 / work:</strong> {record.work_content_md}</div>}
      {record.work_qty_md && <div className="small" style={{ marginBottom: 6 }}><strong>工作量 / quantity:</strong> {record.work_qty_md}</div>}

      <RowsEditor title="人员 / Crew" cols={['name', 'trade', 'grade', 'hours']} labels={['姓名', '工种', '级别', '工时']} rows={persons} setRows={setPersons} />
      <RowsEditor title="材料 / Materials" cols={['name', 'category', 'spec', 'brand', 'qty', 'unit']} labels={['名称', '类别', '规格', '品牌', '数量', '单位']} rows={materials} setRows={setMaterials} />
      <RowsEditor title="机械 / Plant" cols={['model', 'qty', 'shifts']} labels={['型号', '数量', '台班']} rows={equipment} setRows={setEquipment} />

      <div className="row-wrap" style={{ gap: 8, alignItems: 'flex-end' }}>
        <div className="field" style={{ flex: '1 1 240px', marginBottom: 0 }}>
          <label>核实说明（必填）/ verification note (required)</label>
          <input value={note} onChange={(e) => setNote(e.target.value)} style={{ minHeight: 44 }} />
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label>核定金额 ¥ / verified amount</label>
          <input type="number" step="0.01" value={priced} onChange={(e) => setPriced(e.target.value)} style={{ minHeight: 44, width: 140 }} />
        </div>
        <div className="field" style={{ marginBottom: 0, flex: '1 1 180px' }}>
          <label>计价依据 / pricing basis</label>
          <input value={basis} onChange={(e) => setBasis(e.target.value)} style={{ minHeight: 44 }} placeholder="例：清单 3-12 行单价 / 零星限价表" />
        </div>
      </div>
      {err && <div className="small" style={{ color: 'var(--bad)', margin: '6px 0' }}>{err}</div>}
      <button className="btn-primary" style={{ minHeight: 44 }} disabled={busy} onClick={submit}>✓ 确认核实 / Confirm</button>
    </div>
  );
}

function RowsEditor({ title, cols, labels, rows, setRows }: {
  title: string; cols: string[]; labels: string[];
  rows: Record<string, unknown>[]; setRows: (v: Record<string, unknown>[]) => void;
}) {
  return (
    <div className="field">
      <label>{title}</label>
      <div className="cmp-wrap">
        <table className="cmp">
          <thead><tr>{labels.map((l) => <th key={l}>{l}</th>)}<th></th></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                {cols.map((c) => (
                  <td key={c}>
                    <input value={String(r[c] ?? '')} style={{ minHeight: 44, minWidth: 80 }}
                      onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, [c]: e.target.value } : x)))} />
                  </td>
                ))}
                <td><button className="small" style={{ minHeight: 44 }} onClick={() => setRows(rows.filter((_, j) => j !== i))}>×</button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={cols.length + 1} className="muted">—</td></tr>}
          </tbody>
        </table>
      </div>
      <button className="small" style={{ minHeight: 44, marginTop: 4 }} onClick={() => setRows([...rows, {}])}>＋ 增加一行 / Add</button>
    </div>
  );
}

// ── 项目经理 branch: price-free, and it never touches a money route ──────────────────────────────

function PmInstructionsView({ pid, me, focusWi, onFocus }: {
  pid: string; me: MeResponse; focusWi: string | null; onFocus: (id: string | null) => void;
}) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['my-work-instructions'], queryFn: myWorkInstructions });
  // One row per instruction — the queue lists the same order once per reason.
  const seen = new Set<string>();
  const rows = (data?.items ?? []).filter((it) => {
    if (it.instruction.package_id !== pid || seen.has(it.instruction.id)) return false;
    seen.add(it.instruction.id);
    return true;
  });
  const reload = () => qc.invalidateQueries({ queryKey: ['my-work-instructions'] });

  if (isLoading) return <div className="card"><Skeleton lines={4} /></div>;
  return (
    <div>
      <div className="card" style={{ marginBottom: 10 }}>
        <strong>📝 工程指令 / Work instructions</strong>
        <div className="small muted" style={{ marginTop: 4 }}>
          项目经理视图：只显示与您有关的指令，且不含任何金额。完整台账由预算/商务查看。/ PM view — the orders you are named on, with no amounts anywhere.
        </div>
      </div>
      {rows.length === 0
        ? <div className="card"><EmptyState title="暂无与您相关的工程指令 / Nothing assigned to you" hint="您担任签字席位的指令会出现在这里，也会出现在「工程指令」导航页。/ orders where you hold a signature seat land here and in the 工程指令 nav page." /></div>
        : (
          <div className="cmp-wrap" style={{ marginBottom: 10 }}>
            <table className="cmp">
              <thead><tr><th>序号</th><th>类型</th><th>状态</th><th>发生日</th><th>报价期限</th><th>操作</th></tr></thead>
              <tbody>
                {rows.map((it) => {
                  const wi = it.instruction;
                  const st = WI_STATE[wi.state];
                  return (
                    <tr key={wi.id}>
                      <td>{wi.doc_no ?? wi.seq}</td>
                      <td>{WI_KIND_LABEL[wi.kind]}</td>
                      <td><span className={`pill ${st.pill} small`}>{st.label}</span></td>
                      <td>{wi.occurred_on}</td>
                      <td><QuoteDueCell d={it} /></td>
                      <td><button className="small" style={{ minHeight: 44 }} onClick={() => onFocus(focusWi === wi.id ? null : wi.id)}>{focusWi === wi.id ? '收起 / Close' : '展开 / Open'}</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      {rows.filter((it) => it.instruction.id === focusWi).map((it) => (
        <PmInstructionCard key={it.instruction.id} item={it} me={me} reload={reload} />
      ))}
    </div>
  );
}

/** The PM's card: scope, quantities, the signature seats, the price-free 指令单. No money, by construction. */
export function PmInstructionCard({ item, me, reload }: { item: MyInstructionItem; me: MeResponse; reload: () => void }) {
  const wi = item.instruction;
  const st = WI_STATE[wi.state];
  return (
    <div className="card" style={{ marginBottom: 12, borderColor: 'var(--accent)' }}>
      <div className="row-wrap" style={{ alignItems: 'center', gap: 10 }}>
        <strong>{wi.doc_no ?? `#${wi.seq}`} · {wi.title}</strong>
        <span className={`pill ${st.pill} small`}>{st.label}</span>
        <span className="small muted">{WI_KIND_LABEL[wi.kind]} · {wi.occurred_on}</span>
        <span style={{ flex: 1 }} />
        {wi.state !== 'draft' && <a className="btn small" style={{ minHeight: 44 }} href={instructionDocumentUrl(wi.id, 'instruction')}>⬇ 指令单 .docx</a>}
      </div>
      {st.note && <div className="small" style={{ color: 'var(--bad)', marginTop: 6 }}>{st.note}</div>}
      {wi.reason_md && <div className="small" style={{ marginTop: 6 }}><strong>事由 / reason:</strong> {wi.reason_md}</div>}
      {wi.scope_md && <div className="small"><strong>内容 / scope:</strong> {wi.scope_md}</div>}
      {wi.location_md && <div className="small"><strong>部位 / location:</strong> {wi.location_md}</div>}
      {item.lines.length > 0 && (
        <div className="cmp-wrap" style={{ marginTop: 8 }}>
          <table className="cmp">
            <thead><tr><th>序号</th><th>名称</th><th>规格</th><th>单位</th><th>数量</th><th>工程量计算式</th></tr></thead>
            <tbody>
              {item.lines.map((l, i) => (
                <tr key={l.id}>
                  {/* 1-based: `seq` is 0-based in the DB, and 「0」 is not a 序号 anybody writes on paper. */}
                  <td>{i + 1}</td><td>{l.item_label}</td><td>{l.spec_md ?? '—'}</td>
                  <td>{l.unit ?? '—'}</td><td>{l.qty ?? '—'}</td><td>{l.measurement_calc_md ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <InstructionSignatureSeats detail={item} me={me} onChanged={reload} />
      {wi.rework_required && !wi.rework_confirmed_at && wi.rework_notified_at && (
        <ReworkConfirm wiid={wi.id} dueAt={wi.rework_confirm_due_at} onDone={reload} />
      )}
    </div>
  );
}
